import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Cpu,
  Keyboard,
  Rocket,
  ShieldCheck,
  Ghost,
  Zap,
  FlaskConical,
} from "lucide-react";

import { mock } from "@/mock";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const TerminalLine = ({ kind, text, dim }) => {
  const isCmd = kind === "cmd";
  return (
    <div className="flex gap-3 text-[13px] leading-6">
      <span
        className={
          "w-3 select-none text-center " +
          (isCmd
            ? "text-emerald-300/80"
            : dim
              ? "text-white/25"
              : "text-white/35")
        }
      >
        {isCmd ? ">" : "·"}
      </span>
      <span
        className={
          (isCmd ? "text-white/90" : "text-white/70") +
          " font-mono"
        }
      >
        {text}
      </span>
    </div>
  );
};

const FeatureGlyph = ({ k }) => {
  const common = "h-4 w-4";
  if (k === "performance") return <Cpu className={common} />;
  if (k === "workflow") return <Keyboard className={common} />;
  if (k === "deploy") return <Rocket className={common} />;
  return <ShieldCheck className={common} />;
};

const Accent = ({ accent }) => {
  const map = {
    green: "from-emerald-500/20 via-emerald-500/5 to-transparent",
    teal: "from-teal-500/20 via-teal-500/5 to-transparent",
    violet: "from-violet-500/20 via-violet-500/5 to-transparent",
    amber: "from-amber-500/20 via-amber-500/5 to-transparent",
  };
  return (
    <div
      aria-hidden="true"
      className={
        "pointer-events-none absolute -inset-px rounded-2xl bg-gradient-to-br " +
        (map[accent] || map.green)
      }
    />
  );
};

export default function Home() {
  const terminalLines = mock.hero.terminal.lines;
  const [visibleCount, setVisibleCount] = useState(0);
  const [cursorOn, setCursorOn] = useState(true);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    let i = 0;
    const reveal = setInterval(() => {
      i += 1;
      setVisibleCount(i);
      if (i >= terminalLines.length) {
        clearInterval(reveal);
      }
    }, 520);

    const blink = setInterval(() => setCursorOn((v) => !v), 520);

    return () => {
      clearInterval(reveal);
      clearInterval(blink);
    };
  }, [terminalLines.length]);

  const bento = useMemo(() => mock.bentoFeatures, []);

  return (
    <div className="pb-20">
      {/* HERO */}
      <section className="relative overflow-hidden px-6 pt-10 sm:px-10 sm:pt-16">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-[-260px] h-[520px] w-[720px] -translate-x-1/2 rounded-full bg-gradient-to-b from-white/10 via-white/5 to-transparent blur-3xl"
        />

        <div className="mx-auto max-w-6xl">
          <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
            <div>
              <Badge className="border-white/10 bg-white/5 text-white/75">
                Developer-first minimalist
              </Badge>

              <h1 className="mt-5 text-balance text-4xl font-semibold tracking-[-0.03em] text-white sm:text-6xl">
                <span className="bg-gradient-to-b from-white via-white/90 to-white/60 bg-clip-text text-transparent">
                  {mock.hero.headline}
                </span>
              </h1>

              <div className="mt-5 text-[13px] font-medium tracking-[0.14em] text-white/55">
                <span className="bg-gradient-to-r from-emerald-200/85 via-white/80 to-violet-200/75 bg-clip-text text-transparent">
                  Create. Share. Collaborate.
                </span>
              </div>

              <p className="mt-3 max-w-xl text-pretty text-[15px] leading-7 text-white/65">
                {mock.hero.subhead}
              </p>

              <div className="mt-7 flex flex-wrap gap-3">
                <Button asChild className="h-11 px-5">
                  <Link to={mock.hero.ctas[0].href}>
                    {mock.hero.ctas[0].label}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>

                <Button
                  asChild
                  variant="secondary"
                  className="h-11 border border-white/10 bg-white/5 px-5 text-white/85 hover:bg-white/10"
                >
                  <Link to={mock.hero.ctas[1].href}>{mock.hero.ctas[1].label}</Link>
                </Button>
              </div>

              {/* highlights moved below to center across both columns */}
            </div>

            <Card className="relative overflow-hidden rounded-2xl border-white/10 bg-white/[0.04] p-0 shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_30px_90px_rgba(0,0,0,0.55)]">
              <div className="flex items-center justify-between border-b border-white/10 bg-white/[0.03] px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-rose-400/80" />
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-300/80" />
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-300/80" />
                </div>
                <div className="text-[12px] text-white/55">
                  {mock.hero.terminal.title}
                </div>
              </div>

              <div className="relative p-4">
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_0%,rgba(16,185,129,0.18),transparent_45%),radial-gradient(circle_at_70%_20%,rgba(139,92,246,0.14),transparent_50%)]"
                />

                <div className="relative space-y-1.5">
                  {terminalLines.slice(0, visibleCount).map((l, idx) => (
                    <TerminalLine
                      key={idx}
                      kind={l.kind}
                      text={l.text}
                      dim={idx < visibleCount - 1 && idx < 1}
                    />
                  ))}

                  <div className="flex items-center gap-2 pt-1 text-[13px] text-white/50">
                    <span className="font-mono">$</span>
                    <span
                      className={
                        "h-[14px] w-[7px] rounded-[2px] bg-white/60 " +
                        (cursorOn ? "opacity-100" : "opacity-20")
                      }
                      style={{ transition: "opacity 220ms ease" }}
                    />
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* BENTO */}
      <section className="mx-auto mt-14 max-w-6xl px-6 sm:px-10">
        <div className="flex items-end justify-between gap-6">
          <div>
            <h2 className="text-2xl font-semibold tracking-[-0.02em] text-white sm:text-3xl">
              Built for teams to collaborate and ship.
            </h2>
            <p className="mt-2 max-w-xl text-[14px] leading-7 text-white/60">
              Connect real, mock & hybrid services, as works-in-progress into collaborative zero-trust overlay environments.
            </p>
          </div>
        </div>

        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          {bento.map((f) => (
            <Card
              key={f.key}
              className="group relative overflow-hidden rounded-2xl border-white/10 bg-white/[0.035] p-0 transition-colors"
            >
              <Accent accent={f.accent} />
              <div className="relative p-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-black/20 text-white/85 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.02)]">
                    <FeatureGlyph k={f.key} />
                  </div>
                  <div>
                    <div className="text-[15px] font-medium text-white">
                      {f.title}
                    </div>
                    <div className="text-[13px] text-white/55">
                      {f.description}
                    </div>
                  </div>
                </div>

                <div className="mt-5 grid gap-2 sm:grid-cols-3">
                  {f.bullets.map((b) => (
                    <div
                      key={b}
                      className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-[12px] text-white/65"
                    >
                      {b}
                    </div>
                  ))}
                </div>


              {/* highlights moved below (centered across both columns) */}
                <div className="mt-5 flex items-center justify-between">
                  <div className="text-[12px] text-white/45">
                    Learn more in Docs
                  </div>
                  <Button
                    asChild
                    variant="ghost"
                    className="h-9 rounded-xl border border-white/10 bg-white/[0.02] text-white/80 hover:bg-white/[0.06]"
                  >
                    <Link to="/docs">
                      Open <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto mt-14 max-w-6xl px-6 sm:px-10">
        <div className="relative overflow-hidden rounded-2xl border border-emerald-400/20 bg-[linear-gradient(120deg,rgba(16,185,129,0.14),rgba(255,255,255,0.03)_55%,rgba(139,92,246,0.10))] p-8 shadow-[0_0_0_1px_rgba(16,185,129,0.06)] backdrop-blur-xl">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(16,185,129,0.18),transparent_45%),radial-gradient(circle_at_75%_70%,rgba(139,92,246,0.16),transparent_45%)]" />
          <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-[13px] font-medium text-white/70">
                Ready when you are.
              </div>
              <div className="mt-1 text-2xl font-semibold tracking-[-0.02em] text-white">
                Download and start shipping today. Start for free.
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                className="h-11 px-5"
                onClick={() => window.dispatchEvent(new Event("smaughk:download"))}
              >
                Download
              </Button>
              <Button
                asChild
                variant="secondary"
                className="h-11 border border-white/15 bg-white/5 px-5 text-white/85 hover:bg-white/10"
              >
                <Link to="/docs">Read docs</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* HERO highlights (centered across both columns) */}
      <section className="mx-auto -mt-6 max-w-6xl px-6 sm:px-10">
        <div className="mx-auto max-w-3xl">
          <div className="grid gap-8 text-center sm:grid-cols-3">
            <div className="flex flex-col items-center">
              <div className="relative">
                <div className="absolute -inset-3 rounded-full bg-emerald-400/10 blur-xl" />
                <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04]">
                  <Ghost className="h-7 w-7 text-emerald-200/90" />
                </div>
              </div>
              <div className="mt-4 text-[14px] font-medium text-white/85">
                Easy Mocking
              </div>
              <div className="mt-1 text-[13px] leading-6 text-white/55">
                Start local. Share instantly.
              </div>
            </div>

            <div className="flex flex-col items-center">
              <div className="relative">
                <div className="absolute -inset-3 rounded-full bg-violet-400/10 blur-xl" />
                <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04]">
                  <Zap className="h-7 w-7 text-violet-200/90" />
                </div>
              </div>
              <div className="mt-4 text-[14px] font-medium text-white/85">
                Real Task Runner
              </div>
              <div className="mt-1 text-[13px] leading-6 text-white/55">
                Deterministic jobs.
              </div>
            </div>

            <div className="flex flex-col items-center">
              <div className="relative">
                <div className="absolute -inset-3 rounded-full bg-amber-400/10 blur-xl" />
                <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04]">
                  <FlaskConical className="h-7 w-7 text-amber-200/90" />
                  <span className="pointer-events-none absolute -right-1 -top-1 h-2 w-2 rounded-full bg-white/30" />
                  <span className="pointer-events-none absolute -right-2 top-3 h-1.5 w-1.5 rounded-full bg-white/20" />
                  <span className="pointer-events-none absolute right-1 -top-3 h-1 w-1 rounded-full bg-white/20" />
                </div>
              </div>
              <div className="mt-4 text-[14px] font-medium text-white/85">
                Synthetic Environments
              </div>
              <div className="mt-1 text-[13px] leading-6 text-white/55">
                Secure staging & demos.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto mt-14 max-w-6xl px-6 sm:px-10">
        <div className="flex items-end justify-between gap-6">
          <div>
            <h2 className="text-2xl font-semibold tracking-[-0.02em] text-white sm:text-3xl">
              Questions, answered.
            </h2>
            <p className="mt-2 text-[14px] leading-7 text-white/60">
              A few things you might be wondering.
            </p>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-2 backdrop-blur-xl">
          <Accordion type="single" collapsible className="w-full">
            {mock.faqs.map((f, idx) => (
              <AccordionItem
                key={idx}
                value={`item-${idx}`}
                className="border-white/10"
              >
                <AccordionTrigger className="px-4 text-left text-[14px] text-white/85 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 text-[14px] leading-7 text-white/60">
                  <div className="whitespace-pre-line">{f.a}</div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
